import styled, { css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';

const sizes = {
  xsmall: css`
    --font-size: 9px;
    --padding: 5px 6px;
  `,
  small: css`
    --font-size: 12px;
    --padding: 8px 12px;
  `,
  medium: css`
    --font-size: 14px;
    --padding: 13px 16px; // This padding needs to visually match the default input field
  `,
  large: css`
    --font-size: 16px;
    --padding: 16px 22px;
  `,
  xlarge: css`
    --font-size: 20px;
    --padding: 20px 32px;
  `,
};

const colors = {
  default: css`
    --background: var(--nude-100);
    --border: var(--nude-100);
    --foreground: #111;
    --hover-background: var(--nude-300);
    --hover-border: var(--nude-300);
    --hover-foreground: #000;
  `,
  primary: css`
    --background: var(--primary-600);
    --border: var(--primary-600);
    --foreground: #fff;
    --hover-background: var(--primary-800);
    --hover-border: var(--primary-800);
    --hover-foreground: #fff;
  `,
  premium: css`
    --background: #222;
    --border: #222;
    --foreground: #fff;
    --hover-background: #000;
    --hover-border: #000;
    --hover-foreground: #fff;
  `,
  success: css`
    --background: var(--success-main);
    --border: var(--success-main);
    --foreground: #fff;
    --hover-background: var(--success-dark);
    --hover-border: var(--success-dark);
    --hover-foreground: #fff;
  `,
  danger: css`
    --background: var(--danger-main);
    --border: var(--danger-main);
    --foreground: #fff;
    --hover-background: var(--danger-main);
    --hover-border: var(--danger-main);
    --hover-foreground: #fff;
  `,
  'danger-outline': css`
    --background: transparent;
    --border: var(--danger-main);
    --foreground: var(--danger-main);
    --hover-background: var(--danger-main);
    --hover-border: var(--danger-main);
    --hover-foreground: #fff;
  `,
  text: css`
    --background: transparent;
    --border: transparent;
    --foreground: currentColor;
    --hover-background: transparent;
    --hover-border: transparent;
    --hover-foreground: currentColor;
  `,
};

const pulseAnimation = keyframes`
  from {box-shadow:0 0 0px 0px var(--background);}
  to {box-shadow:0 0 0px 1.3em transparent;}
`;

export const ButtonStyles = css`
  ${p => colors[p.color || 'default']}
  ${p => sizes[p.size || 'medium']}
  background-color: var(--background);
  border-radius: 4px;
  border: solid 1px var(--border);
  color: var(--foreground);
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: ${p => (p.block ? 'flex' : 'inline-flex')};
  font-size: var(--font-size);
  font-weight: bold;
  justify-content: center;
  line-height: var(--font-size);
  /* margin: 0 auto; */ // Removed this, not sure what it's going to break
  opacity: ${props => (props.disabled ? '.6' : '1')};
  padding: var(--padding);
  position: relative;
  text-align: center;
  text-decoration: none;
  width: ${p => (p.block ? '100%' : 'auto')};

  ${p =>
    p.pulse === true &&
    css`
      animation: ${pulseAnimation} 3s 2s infinite;
    `}

  &:hover {
    background: var(--hover-background);
    border-color: var(--hover-border);
    color: var(--hover-foreground);
  }

  &:focus {
    transform: scale(1);
    box-shadow: none;
  }

  &:active {
    box-shadow: inset 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const ActionButton = styled.button.attrs(p => ({
  className: 'btn',
  type: p.type || 'submit',
}))`
  ${ButtonStyles}
`;

ActionButton.displayName = 'ActionButton';
ActionButton.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'default',
    'premium',
    'success',
    'danger',
    'text',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  onClick: PropTypes.func,
};

export const LinkButton = styled.a.attrs(p => ({
  className: 'btn',
}))`
  ${ButtonStyles}
`;
LinkButton.displayName = 'LinkButton';
LinkButton.description =
  'Button component used for generic navigation, a styled anchor tag essentially';
LinkButton.propTypes = {
  /**
   * Is this the principal call to action on the page?
   */
  // primary: PropTypes.bool,
  /**
   * What background color to use
   */
  color: PropTypes.oneOf([
    'primary',
    'default',
    'premium',
    'success',
    'danger',
    'text',
  ]),
  /**
   * How large should the button be?
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   * Button contents
   */
  // label: PropTypes.string.isRequired,
  /**
   * Optional click handler
   */
  onClick: PropTypes.func,
};
